/**
 * Phase 20 / Task 20.7 — §80 "Import history" (AC-5): every import this tenant
 * has run, newest first, and one import's detail with `[ Download result ]`.
 *
 * The list is `GET /v1/imports` (the API's newest 100). The detail combines two
 * reads: `GET /v1/imports/:id` for the file and its sheets as staged, and
 * `GET …/commit-status` for what the commit did per entity, including §80's skip
 * reasons. The second is the same call the wizard's run step polls, so a history
 * row and a finished run step can never disagree about a count.
 *
 * `[ Download result ]` re-signs the result workbook on every click
 * (`GET …/result-file`): the stored value is a key, never a URL, so last
 * Tuesday's import still downloads. It is offered only when the commit left a
 * file (`hasResultFile`) — a dry run or an import that never committed has none.
 *
 * ── Time ───────────────────────────────────────────────────────────────────
 * DEC-004: rendered in the viewer's profile timezone, never UTC. History spans
 * months, so the year is shown, which the board's `formatTimestamp` leaves out.
 *
 * These routes return Postgres's text form (`2026-10-08 02:14:39.327996+00`),
 * not ISO. Chrome's Date parser accepts it; Safari's does not, and every time
 * on the page would read "—". `toIsoInstant` rewrites it first.
 *
 * ── Always fresh on open ───────────────────────────────────────────────────
 * The app's query cache keeps data fresh for a while, so a history opened again
 * after an undo showed the batch as still Imported (found on dev). Opening the
 * list or a detail always refetches.
 */

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiOpts } from '@lodex-pro/web-shared';

import { useUserTimezone } from '../../lib/use-user-timezone';
import { HISTORY_LIMIT, getCommitStatus, getImport, getResultFile, listImports } from './api';
import { familyLabel } from './mapping-state';
import { exampleRowSentence, exampleRowsFromSummary } from './example-row';
import type { BatchStatus, CommitReport, ImportBatchSummary } from './types';
import { ActionButton, Notice, Panel, TONE, TOUCH_MIN, muted, plural } from './ui';

/**
 * `2026-10-08 02:14:39.327996+00` -> `2026-10-08T02:14:39.327+00:00`. An ISO
 * string passes through unchanged.
 */
export function toIsoInstant(s: string): string {
  const m =
    /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(\.\d+)?\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/.exec(
      s.trim(),
    );
  if (m === null) return s;
  const [, date, time, frac, zone] = m;
  const ms = frac === undefined ? '' : frac.slice(0, 4);
  let tz = zone ?? 'Z';
  if (/^[+-]\d{2}$/.test(tz)) tz = `${tz}:00`;
  else if (/^[+-]\d{4}$/.test(tz)) tz = `${tz.slice(0, 3)}:${tz.slice(3)}`;
  return `${date ?? ''}T${time ?? ''}${ms}${tz}`;
}

/** "Oct 7, 2026, 2:14 PM" in the viewer's zone. */
export function formatWhen(iso: string | null, timeZone: string): string {
  if (iso === null) return '—';
  const d = new Date(toIsoInstant(iso));
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(d);
}

const STATUS_TEXT: Record<
  BatchStatus,
  { readonly text: string; readonly tone: keyof typeof TONE }
> = {
  PENDING: { text: 'Not imported', tone: 'warn' },
  VALIDATING: { text: 'Checking', tone: 'warn' },
  PREVIEW: { text: 'Not imported', tone: 'warn' },
  IMPORTING: { text: 'Importing', tone: 'warn' },
  COMPLETE: { text: 'Imported', tone: 'ok' },
  FAILED: { text: 'Failed', tone: 'error' },
  CANCELLED: { text: 'Cancelled', tone: 'warn' },
  UNDONE: { text: 'Undone', tone: 'warn' },
};

export function statusLabel(status: BatchStatus, isDryRun: boolean): string {
  const base = STATUS_TEXT[status].text;
  return isDryRun ? `Test run — ${base.toLowerCase()}` : base;
}

function StatusChip(props: {
  readonly status: BatchStatus;
  readonly isDryRun: boolean;
}): React.JSX.Element {
  const tone = TONE[STATUS_TEXT[props.status].tone];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 999,
        fontSize: 14,
        backgroundColor: tone.bg,
        border: `1px solid ${tone.border}`,
      }}
    >
      {statusLabel(props.status, props.isDryRun)}
    </span>
  );
}

function families(b: ImportBatchSummary): string {
  const list = b.entityFamilies ?? [];
  return list.length === 0 ? 'No sheets' : list.map(familyLabel).join(', ');
}

function ReportTable(props: { readonly report: CommitReport }): React.JSX.Element {
  const cell: React.CSSProperties = {
    padding: '8px 12px',
    borderBottom: '1px solid var(--color-border-primary)',
    textAlign: 'right',
  };
  return (
    <table style={{ borderCollapse: 'collapse', fontSize: 16, width: '100%' }}>
      <thead>
        <tr>
          <th style={{ ...cell, textAlign: 'left' }}>What</th>
          <th style={cell}>Created</th>
          <th style={cell}>Updated</th>
          <th style={cell}>Unchanged</th>
          <th style={cell}>Skipped</th>
          <th style={cell}>Failed</th>
        </tr>
      </thead>
      <tbody>
        {props.report.families.map((f) => (
          <tr key={f.entityFamily}>
            <td style={{ ...cell, textAlign: 'left' }}>{familyLabel(f.entityFamily)}</td>
            <td style={cell}>{f.imported}</td>
            <td style={cell}>{f.updated}</td>
            <td style={cell}>{f.matched}</td>
            <td style={cell}>{f.skipped}</td>
            <td style={cell}>{f.failed}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ImportDetail(props: {
  readonly batchId: string;
  readonly onBack: () => void;
}): React.JSX.Element {
  const apiOpts = useApiOpts();
  const tz = useUserTimezone();
  const batch = useQuery({
    queryKey: ['imports', props.batchId, 'detail'],
    refetchOnMount: 'always',
    queryFn: () => getImport(apiOpts, props.batchId),
  });
  const status = useQuery({
    queryKey: ['imports', props.batchId, 'commit-status'],
    refetchOnMount: 'always',
    queryFn: () => getCommitStatus(apiOpts, props.batchId),
  });
  const [busy, setBusy] = React.useState(false);
  const [downloadError, setDownloadError] = React.useState<string | null>(null);

  const download = async (): Promise<void> => {
    setBusy(true);
    setDownloadError(null);
    try {
      const file = await getResultFile(apiOpts, props.batchId);
      window.open(file.downloadUrl, '_blank', 'noopener');
    } catch (err: unknown) {
      setDownloadError(err instanceof Error ? err.message : 'The result file could not be found.');
    } finally {
      setBusy(false);
    }
  };

  const back = <ActionButton onClick={props.onBack}>Back to import history</ActionButton>;

  if (batch.isPending) return <Panel>Loading the import…</Panel>;
  if (batch.isError) {
    return (
      <Panel>
        <Notice tone="error" role="alert">
          {batch.error.message}
        </Notice>
        <div style={{ marginTop: 16 }}>{back}</div>
      </Panel>
    );
  }

  const b = batch.data;
  const report = status.data?.report ?? null;
  const reasons = report?.skippedReasons ?? [];
  const exampleRows = exampleRowsFromSummary(b.errorSummary);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Panel labelledBy="import-detail-heading">
        <h2
          id="import-detail-heading"
          style={{ fontSize: 18, fontWeight: 600, overflowWrap: 'anywhere' }}
        >
          {b.fileName ?? 'Import'}
        </h2>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
          <StatusChip status={b.status} isDryRun={b.isDryRun} />
          <span style={muted}>Uploaded {formatWhen(b.createdAt, tz)}</span>
          {b.completedAt !== null && (
            <span style={muted}>Finished {formatWhen(b.completedAt, tz)}</span>
          )}
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginTop: 16 }}>Sheets</h3>
        {b.sheets.length === 0 ? (
          <p style={muted}>
            The row-by-row staging for this import has been cleared (it is kept for 7 days). The
            counts below and the result file are kept.
          </p>
        ) : (
          <ul style={{ padding: 0, margin: '8px 0 0' }}>
            {b.sheets.map((s) => (
              <li key={`${s.sheetName}|${s.entityFamily}`} style={{ listStyle: 'none' }}>
                {s.sheetName === '' ? 'This file' : `"${s.sheetName}"`} →{' '}
                {familyLabel(s.entityFamily)} ({plural(s.rowCount, 'row', 'rows')})
              </li>
            ))}
          </ul>
        )}
        {exampleRows.length > 0 && (
          <ul
            data-testid="import-example-rows-skipped"
            style={{ padding: 0, margin: '8px 0 0', overflowWrap: 'anywhere' }}
          >
            {exampleRows.map((r) => (
              <li key={`${r.sheetName}|${r.rowNumber.toString()}`} style={{ listStyle: 'none' }}>
                {r.sheetName === '' ? '' : `"${r.sheetName}": `}
                {exampleRowSentence(r)}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel labelledBy="import-detail-result">
        <h3 id="import-detail-result" style={{ fontSize: 16, fontWeight: 600 }}>
          Result
        </h3>
        {status.isPending && <p style={muted}>Loading the result…</p>}
        {status.isError && (
          <Notice tone="error" role="alert">
            {status.error.message}
          </Notice>
        )}
        {status.data !== undefined && report === null && (
          <p style={{ ...muted, marginTop: 8 }}>
            {status.data.commitState === null
              ? 'This file was checked but never imported. Nothing was saved.'
              : 'This import has not finished.'}
          </p>
        )}
        {status.data?.error != null && (
          <div style={{ marginTop: 8 }}>
            <Notice tone="error">{status.data.error}</Notice>
          </div>
        )}
        {report !== null && (
          <div style={{ marginTop: 8, overflowX: 'auto' }}>
            <ReportTable report={report} />
          </div>
        )}
        {reasons.length > 0 && (
          <>
            <h4 style={{ fontSize: 16, fontWeight: 600, marginTop: 16 }}>Why rows were skipped</h4>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </>
        )}
        {downloadError !== null && (
          <div style={{ marginTop: 12 }}>
            <Notice tone="error" role="alert">
              {downloadError}
            </Notice>
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          {status.data?.hasResultFile === true && (
            <ActionButton
              variant="primary"
              disabled={busy}
              onClick={(): void => {
                void download();
              }}
            >
              {busy ? 'Preparing…' : 'Download result'}
            </ActionButton>
          )}
          {back}
        </div>
      </Panel>
    </div>
  );
}

export function HistoryPanel(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const tz = useUserTimezone();
  const [open, setOpen] = React.useState<string | null>(null);
  const query = useQuery({
    queryKey: ['imports', 'history'],
    refetchOnMount: 'always',
    queryFn: () => listImports(apiOpts),
  });

  if (open !== null) return <ImportDetail batchId={open} onBack={(): void => setOpen(null)} />;

  return (
    <Panel labelledBy="import-history-heading">
      <h2 id="import-history-heading" style={{ fontSize: 18, fontWeight: 600 }}>
        Import history
      </h2>
      {query.isPending && <p style={{ ...muted, marginTop: 8 }}>Loading…</p>}
      {query.isError && (
        <div style={{ marginTop: 12 }}>
          <Notice tone="error" role="alert">
            {query.error.message}
          </Notice>
        </div>
      )}
      {query.data?.batches.length === 0 && (
        <p style={{ ...muted, marginTop: 8 }}>No imports yet. Start one with + New import.</p>
      )}
      {query.data !== undefined && query.data.batches.length >= HISTORY_LIMIT && (
        <p style={{ ...muted, marginTop: 8 }}>
          Showing the newest {HISTORY_LIMIT.toString()} imports.
        </p>
      )}
      <ul style={{ padding: 0, margin: '12px 0 0', display: 'flex', flexDirection: 'column' }}>
        {(query.data?.batches ?? []).map((b) => (
          <li key={b.id} style={{ listStyle: 'none' }}>
            <button
              type="button"
              onClick={(): void => setOpen(b.id)}
              style={{
                width: '100%',
                minHeight: TOUCH_MIN,
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 16,
                color: 'var(--color-text-primary)',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--color-border-primary)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <span style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ overflowWrap: 'anywhere' }}>{b.fileName ?? 'Import'}</strong>
                <StatusChip status={b.status} isDryRun={b.isDryRun} />
              </span>
              <span style={muted}>
                {formatWhen(b.createdAt, tz)}
                {b.importedByName !== null ? ` · ${b.importedByName}` : ''} · {families(b)}
              </span>
              <span style={muted}>
                {plural(b.totalRows, 'row', 'rows')} · {b.rowsCreated} created · {b.rowsUpdated}{' '}
                updated · {b.rowsSkipped} skipped · {b.rowsFailed} failed
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
